import { useRef, useState, ReactNode, MouseEvent, TouchEvent } from 'react';

/**
 * A list row you can swipe on a touch screen.
 *
 * Swipe left  → onSwipeLeft  (we use it for "mark read")
 * Swipe right → onSwipeRight (we use it for "mark unread")
 *
 * The row follows the finger, a mono label shows what will happen, and the
 * action fires once the finger lifts past the threshold. Vertical scrolling
 * is left alone: the gesture only takes over once it's clearly horizontal.
 * A completed swipe swallows the click that would otherwise open the article.
 */
interface SwipeRowProps {
  leftLabel?: string;   // shown while swiping left; omit to disable left swipe
  rightLabel?: string;  // shown while swiping right; omit to disable right swipe
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
}

const THRESHOLD_PX = 72;   // how far the finger must travel to commit
const LOCK_PX = 12;        // how far before we decide it's a horizontal gesture
const MAX_PULL_PX = 120;   // the row never travels further than this

export default function SwipeRow({
  leftLabel, rightLabel, onSwipeLeft, onSwipeRight, onClick, className, style, children,
}: SwipeRowProps) {
  const start = useRef<{ x: number; y: number } | null>(null);
  const axis = useRef<'none' | 'x' | 'y'>('none');
  const swallowClick = useRef(false);
  const [dx, setDx] = useState(0);
  const [settling, setSettling] = useState(false);

  const canLeft = Boolean(leftLabel && onSwipeLeft);
  const canRight = Boolean(rightLabel && onSwipeRight);

  function onTouchStart(e: TouchEvent) {
    if (e.touches.length !== 1) return;
    start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    axis.current = 'none';
    setSettling(false);
  }

  function onTouchMove(e: TouchEvent) {
    if (!start.current) return;
    const x = e.touches[0].clientX - start.current.x;
    const y = e.touches[0].clientY - start.current.y;
    if (axis.current === 'none') {
      if (Math.abs(x) < LOCK_PX && Math.abs(y) < LOCK_PX) return;
      axis.current = Math.abs(x) > Math.abs(y) ? 'x' : 'y';
    }
    if (axis.current !== 'x') return;
    // Only let the row travel in a direction that has an action.
    const clamped = Math.max(-MAX_PULL_PX, Math.min(MAX_PULL_PX, x));
    if (clamped < 0 && !canLeft) { setDx(0); return; }
    if (clamped > 0 && !canRight) { setDx(0); return; }
    setDx(clamped);
  }

  function onTouchEnd() {
    if (!start.current) return;
    const committed = axis.current === 'x' && Math.abs(dx) >= THRESHOLD_PX;
    if (axis.current === 'x' && Math.abs(dx) > LOCK_PX) {
      swallowClick.current = true;
      // Let the synthetic click that follows touchend hit the guard, then reset.
      setTimeout(() => { swallowClick.current = false; }, 400);
    }
    if (committed) {
      if (dx < 0) onSwipeLeft?.();
      else onSwipeRight?.();
    }
    start.current = null;
    axis.current = 'none';
    setSettling(true);
    setDx(0);
  }

  function handleClick(e: MouseEvent) {
    if (swallowClick.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.();
  }

  const armed = Math.abs(dx) >= THRESHOLD_PX;
  const label = dx < 0 ? leftLabel : rightLabel;

  return (
    <div className="relative overflow-hidden" style={{ touchAction: 'pan-y' }}>
      {/* Backdrop label, only visible while the row is pulled aside */}
      {dx !== 0 && (
        <div
          className={`absolute inset-y-0 flex items-center px-4 kicker ${
            dx < 0 ? 'right-0 justify-end' : 'left-0 justify-start'
          } ${armed ? 'text-canvas bg-ink' : 'text-ink bg-surface-sunken'}`}
          style={{ width: Math.abs(dx) }}
          aria-hidden="true"
        >
          <span className="whitespace-nowrap">{dx < 0 ? `← ${label}` : `${label} →`}</span>
        </div>
      )}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onClick={handleClick}
        className={className}
        style={{
          ...style,
          transform: `translateX(${dx}px)`,
          transition: settling ? 'transform 160ms ease-out' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}
